import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Wand2 } from "lucide-react";
import { toast } from "sonner";
import { inr } from "@/lib/utils2";

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

/**
 * Generic "settle on-account balance against open items" dialog.
 * loadRows(entityId) => { onAccount, rows: [{ id, balance, ...cells }] } (rows oldest first)
 */
export default function SettleDialog({
  open, onOpenChange, title, entityLabel, entities, initialEntityId = "",
  loadRows, columns, endpoint, allocKey, onDone, testIdPrefix = "settle",
}) {
  const [entityId, setEntityId] = useState(initialEntityId);
  const [onAccount, setOnAccount] = useState(0);
  const [rows, setRows] = useState([]);
  const [allocs, setAllocs] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setEntityId(initialEntityId); setAllocs({}); } }, [open, initialEntityId]);

  useEffect(() => {
    if (!entityId) { setRows([]); setOnAccount(0); return; }
    setAllocs({});
    loadRows(entityId).then((d) => { setRows(d.rows); setOnAccount(d.onAccount); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  const allocTotal = useMemo(() => r2(Object.values(allocs).reduce((s, v) => s + (Number(v) || 0), 0)), [allocs]);
  const remaining = r2(onAccount - allocTotal);

  const toggle = (row, checked) => {
    setAllocs((s) => {
      const n = { ...s };
      if (checked) n[row.id] = r2(Math.min(row.balance, Math.max(remaining, 0)));
      else delete n[row.id];
      return n;
    });
  };
  const setAmt = (row, v) => setAllocs((s) => ({ ...s, [row.id]: r2(Math.min(Math.max(parseFloat(v) || 0, 0), row.balance)) }));

  const autoSettle = () => {
    let left = onAccount;
    const n = {};
    for (const row of rows) {
      if (left <= 0.001) break;
      const take = r2(Math.min(row.balance, left));
      if (take > 0) { n[row.id] = take; left = r2(left - take); }
    }
    setAllocs(n);
  };

  const save = async () => {
    const allocations = Object.entries(allocs).filter(([, v]) => v > 0).map(([id, amount]) => ({ [allocKey]: id, amount }));
    if (!allocations.length) return toast.error("Select at least one item to settle");
    if (allocTotal > onAccount + 0.01) return toast.error("Settle amount exceeds available on-account balance");
    setSaving(true);
    try {
      const { data } = await api.post(endpoint(entityId), { allocations });
      toast.success(`Settled ${inr(data.settled)} · on-account left ${inr(data.on_account_left)}`);
      onOpenChange(false); onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Settlement failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="rounded-sm max-w-3xl" data-testid={`${testIdPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{title}</DialogTitle>
          <div className="text-xs text-brand-muted">
            Uses unapplied on-account money from ALL payments of this {entityLabel.toLowerCase()} — oldest payment is consumed first.
          </div>
        </DialogHeader>
        <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
          <div className="grid grid-cols-2 gap-4 items-end">
            <div>
              <Label>{entityLabel} *</Label>
              <Select value={entityId} onValueChange={setEntityId}>
                <SelectTrigger data-testid={`${testIdPrefix}-entity-select`} className="mt-1.5 h-10 rounded-sm bg-white"><SelectValue placeholder={`Select ${entityLabel.toLowerCase()}`} /></SelectTrigger>
                <SelectContent>{entities.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {entityId && (
              <div className="rounded-sm border border-brand-sand/50 bg-brand-sand/10 px-4 py-2.5 text-xs flex items-center justify-between gap-3">
                <div>
                  <div className="uppercase tracking-[0.15em] text-[10px] text-brand-muted">On-account available</div>
                  <div className="font-serif text-xl tabular-nums text-brand-ink" data-testid={`${testIdPrefix}-onaccount`}>{inr(onAccount)}</div>
                </div>
                <div className="text-right">
                  <div className="uppercase tracking-[0.15em] text-[10px] text-brand-muted">Remaining after settle</div>
                  <div className={`font-semibold tabular-nums ${remaining < -0.01 ? "text-brand-terracotta" : "text-brand-forest"}`} data-testid={`${testIdPrefix}-remaining`}>{inr(remaining)}</div>
                </div>
              </div>
            )}
          </div>

          {entityId && (
            onAccount <= 0.01 ? (
              <div className="text-xs text-brand-muted p-4 text-center bg-secondary/40 rounded-sm">No on-account balance to settle for this {entityLabel.toLowerCase()}.</div>
            ) : rows.length === 0 ? (
              <div className="text-xs text-brand-muted p-4 text-center bg-secondary/40 rounded-sm">Nothing outstanding — everything is already settled.</div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-brand-muted">Tick items to settle · Allocated {inr(allocTotal)}</div>
                  <Button size="sm" variant="outline" onClick={autoSettle} data-testid={`${testIdPrefix}-auto-btn`} className="h-7 rounded-sm text-[11px]">
                    <Wand2 className="h-3 w-3 mr-1" /> Auto-settle oldest first
                  </Button>
                </div>
                <div className="border border-brand-line rounded-sm">
                  <table className="w-full text-xs">
                    <thead className="bg-secondary/60 text-[10px] uppercase tracking-[0.15em] text-brand-muted">
                      <tr>
                        <th className="px-2 py-2 w-8"></th>
                        {columns.map((c) => <th key={c.key} className={`px-2 py-2 ${c.align === "right" ? "text-right" : "text-left"}`}>{c.label}</th>)}
                        <th className="text-right px-2 py-2">Balance</th>
                        <th className="text-right px-2 py-2 w-32">Settle Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => {
                        const checked = row.id in allocs;
                        return (
                          <tr key={row.id} className={`border-t border-brand-line ${checked ? "bg-brand-sand/10" : ""}`}>
                            <td className="px-2 py-2"><Checkbox checked={checked} onCheckedChange={(v) => toggle(row, !!v)} data-testid={`${testIdPrefix}-row-${row.id}`} /></td>
                            {columns.map((c) => (
                              <td key={c.key} className={`px-2 py-2 ${c.align === "right" ? "text-right tabular-nums" : ""} ${c.className || ""}`}>
                                {c.render ? c.render(row) : row[c.key]}
                              </td>
                            ))}
                            <td className="px-2 py-2 text-right tabular-nums text-brand-terracotta">{inr(row.balance)}</td>
                            <td className="px-2 py-2 text-right">
                              {checked && (
                                <Input type="number" step="0.01" min="0" max={row.balance} value={allocs[row.id]}
                                  onChange={(e) => setAmt(row, e.target.value)}
                                  data-testid={`${testIdPrefix}-amt-${row.id}`}
                                  className="h-8 w-28 rounded-sm text-right tabular-nums bg-white text-xs ml-auto" />
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={save} disabled={saving || !entityId || allocTotal <= 0 || remaining < -0.01}
            data-testid={`${testIdPrefix}-save-btn`} className="rounded-sm bg-brand-forest text-white">
            {saving ? "Settling…" : `Settle ${inr(allocTotal)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
